/*
 * Journey step: choose your hardware.
 *
 * Deliberately NOT a reimplementation of the hardware calculator. It reads the
 * same two shop metafields the standalone calculator reads:
 *
 *   custom_calculator.settings         33 rules, filtered by type and options
 *   custom_calculator.product_details  how a computed amount becomes line items
 *
 * so the merchant keeps one rule set. Editing the metafields updates the
 * calculator and this step together. The resolution types below mirror the
 * calculator's behaviour exactly, including that move_up always adds a single
 * larger pack rather than several small ones.
 *
 * What the customer does not get asked again: area type, length, width, ends
 * and end dimensions all come from the measure step. Only the things measure
 * cannot know are asked here, which is materials, build style and post count.
 */
(function () {
  'use strict';

  /**
   * Evaluate a merchant-authored arithmetic expression with the measurement
   * variables substituted in.
   *
   * The standalone calculator calls eval() on these directly. This does not.
   * The metafield is merchant data, but it reaches the browser as page content,
   * so the expression is whitelisted to arithmetic before it is evaluated.
   */
  function evaluate(expression, vars) {
    if (typeof expression !== 'string') return NaN;

    var substituted = expression.replace(/\b(we1|he1|we2|he2|l|w|p|s)\b/g, function (name) {
      var value = vars[name];
      return typeof value === 'number' && isFinite(value) ? String(value) : '0';
    });

    if (!/^[0-9+\-*/(). ]+$/.test(substituted)) return NaN;

    try {
      // eslint-disable-next-line no-new-func
      var result = new Function('return (' + substituted + ');')();
      return typeof result === 'number' && isFinite(result) ? result : NaN;
    } catch (err) {
      return NaN;
    }
  }

  /**
   * Turn one { id, calc } into line items, using the product's detail entry.
   * Mirrors the calculator's four strategies.
   *
   * @returns {Array<{handle:string, sku:string, quantity:number, raw:number}>}
   */
  function resolve(entry, detail, vars) {
    var raw = Math.ceil(evaluate(entry.calc, vars));
    if (!isFinite(raw) || raw <= 0) return [];

    var out = [];

    if (detail.type === 'general') {
      out.push({ handle: detail.handle, sku: detail.sku, quantity: raw, raw: raw });
    } else if (detail.type === 'calc_quant') {
      // Smallest bracket the amount fits under wins.
      var chosen = 0;
      var brackets = detail.calc_quant.slice().sort(function (a, b) {
        return b.less_than - a.less_than;
      });
      for (var b = 0; b < brackets.length; b++) {
        if (raw < brackets[b].less_than) chosen = brackets[b].quant;
      }
      if (chosen > 0) out.push({ handle: detail.handle, sku: detail.sku, quantity: chosen, raw: raw });
    } else if (detail.type === 'move_up') {
      // Step up to a bigger pack rather than buying more of a small one.
      var index = 0;
      for (var m = 0; m < detail.move_to_next.length; m++) {
        if (raw >= detail.move_to_next[m]) index++;
      }
      var pack = detail.multi_quant[Math.min(index, detail.multi_quant.length - 1)];
      if (pack) out.push({ handle: pack.handle, sku: pack.sku, quantity: 1, raw: raw });
    } else if (detail.type === 'tabulate') {
      // Greedy pack, biggest first.
      var remaining = raw;
      var packs = detail.multi_quant.slice().sort(function (a, b) {
        return b.amount - a.amount;
      });
      for (var t = 0; t < packs.length; t++) {
        var count = Math.floor(remaining / packs[t].amount);
        if (count > 0) {
          out.push({ handle: packs[t].handle, sku: packs[t].sku, quantity: count, raw: raw });
          remaining -= count * packs[t].amount;
        }
      }
    } else if (detail.type === 'always') {
      out.push({ handle: detail.handle, sku: detail.sku, quantity: detail.always_quant || 1, raw: raw });
    } else if (detail.type === 'over_add' && detail.over_add) {
      // The base pack always goes in; a second product joins it once the
      // amount reaches the threshold (NZ's clips: mini pack, plus a bag of 500).
      out.push({ handle: detail.handle, sku: detail.sku, quantity: detail.always_quant || 1, raw: raw });
      if (raw >= detail.over_add.over_quant) {
        out.push({ handle: detail.over_add.handle, sku: detail.over_add.sku, quantity: detail.over_add.quant, raw: raw });
      }
    }

    return out;
  }

  /** Alley pairs are stored one way round only, so normalise before matching. */
  function alleyMaterials(m1, m2) {
    var a = (m1 || '').replace('other', 'colorbond');
    var b = (m2 || '').replace('other', 'colorbond');
    var key = a + '-' + b;

    var swaps = {
      'colorbond-brick': 'brick-colorbond',
      'colorbond-wood': 'wood-colorbond',
      'brick-wood': 'wood-brick'
    };

    return swaps[key] || key;
  }

  function Hardware(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.stepId = this.config.stepId || 'hardware';
    this.measureStepId = this.config.measureStepId || 'measure';
    this.labels = this.config.labels || {};

    this.introEl = root.querySelector('[data-hw-intro]');
    this.questionsEl = root.querySelector('[data-hw-questions]');
    this.resultEl = root.querySelector('[data-hw-result]');
    this.rowsEl = root.querySelector('[data-hw-rows]');
    this.totalEl = root.querySelector('[data-hw-total]');
    this.unsupportedEl = root.querySelector('[data-hw-unsupported]');

    this.answers = {};
    this.overrides = {};
    this.journey = null;

    this.money = this.moneyFormatter();
    this.bind();
  }

  Hardware.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-hw-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Hardware.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'AUD';
    try {
      var fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (err) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  };

  Hardware.prototype.bind = function () {
    var self = this;

    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (e) {
        self.attach(e.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (e) {
      if (e.detail.step === self.stepId) self.refresh();
    });

    document.addEventListener('journey:step:complete', function (e) {
      // Measurements moved, so the hardware list is stale.
      if (e.detail.step === self.measureStepId) {
        self.overrides = {};
        self.refresh();
      }
    });

    this.root.addEventListener('change', function (e) {
      if (e.target.matches('[data-hw-answer]')) {
        self.answers[e.target.getAttribute('data-hw-answer')] = e.target.value;
        self.overrides = {};
        self.renderQuestions();
        self.renderResult();
      }
    });

    this.root.addEventListener('input', function (e) {
      if (e.target.matches('[data-hw-qty]')) {
        var key = e.target.getAttribute('data-hw-qty');
        var value = parseInt(e.target.value, 10);
        self.overrides[key] = isFinite(value) && value >= 0 ? value : 0;
        self.renderTotals();
        self.report();
      }
    });
  };

  Hardware.prototype.attach = function (journey) {
    this.journey = journey;

    var saved = journey.data(this.stepId);
    if (saved && saved.answers) {
      this.answers = saved.answers;
      this.overrides = saved.overrides || {};
    }

    this.refresh();
  };

  Hardware.prototype.measure = function () {
    return this.journey ? this.journey.data(this.measureStepId) : null;
  };

  /** Rules for the measured area type. Empty means we cannot spec this area. */
  /**
   * An area can borrow another area's rule set without the metafields knowing.
   * Kurt's call: a backyard is hardware-wise identical to a balcony, four
   * surfaces and a net across the top, so "backyard:balcony" (the default)
   * makes backyard use the balcony rules. The standalone calculator and its
   * metafields are untouched; the alias exists only in this step.
   *
   * Config format: "backyard:balcony, other-area:existing-type".
   */
  Hardware.prototype.ruleType = function (area) {
    if (!this.aliases) {
      this.aliases = {};
      var raw = String(this.config.areaAliases || '');
      var pairs = raw.split(',');
      for (var i = 0; i < pairs.length; i++) {
        var bits = pairs[i].split(':');
        if (bits.length === 2) {
          this.aliases[bits[0].trim()] = bits[1].trim();
        }
      }
    }
    return this.aliases[area] || area;
  };

  Hardware.prototype.rulesForType = function (type) {
    var out = [];
    for (var i = 0; i < this.config.rules.length; i++) {
      if (this.config.rules[i].type === type) out.push(this.config.rules[i]);
    }
    return out;
  };

  /**
   * Which questions apply, given the area type and what has been answered.
   * Style only appears where an economical build is actually offered.
   */
  Hardware.prototype.questions = function (area) {
    var out = [];
    var materials = this.config.materialOptions;
    var type = this.ruleType(area);
    var aliased = type !== area;

    if (type === 'alley') {
      // Named surfaces, matching the standalone calculator's "Fence Side" and
      // "House Side". Generic "side 1 / side 2" left customers guessing which
      // wall was which, and material drives the fixings.
      out.push({ id: 'm1', label: this.labels.alleyFence || 'What is the fence side made of?', options: materials });
      out.push({ id: 'm2', label: this.labels.alleyHouse || 'What is the house side made of?', options: materials });
    } else if (type === 'balcony') {
      if (aliased) {
        // An aliased area (backyard) has four sides rather than walls, roof
        // and floor. Same maths either way: m1/m2 weigh in at the width and
        // m3/m4 at the length, which for a backyard is its two short sides
        // and two long sides.
        out.push({ id: 'm1', label: this.labels.side1 || 'Side 1 material', options: materials });
        out.push({ id: 'm2', label: this.labels.side2 || 'Side 2 material', options: materials });
        out.push({ id: 'm3', label: this.labels.side3 || 'Side 3 material', options: materials });
        out.push({ id: 'm4', label: this.labels.side4 || 'Side 4 material', options: materials });
      } else {
        out.push({ id: 'm1', label: this.labels.balconySide1 || 'What is side wall 1 made of?', options: materials });
        out.push({ id: 'm2', label: this.labels.balconySide2 || 'What is side wall 2 made of?', options: materials });
        out.push({ id: 'm3', label: this.labels.roof || 'Roof material', options: materials });
        out.push({ id: 'm4', label: this.labels.floor || 'Floor material', options: materials });
      }
    } else if (type === 'fenceline') {
      out.push({ id: 'm1', label: this.labels.fence || 'Fence material', options: materials });
      out.push({
        id: 'p',
        label: this.labels.posts || 'How many Fence Extension Brackets?',
        help: this.labels.postsHelp || '',
        type: 'number',
        min: 1,
        max: 200
      });
    }

    if (this.styleOffered(type)) {
      out.push({ id: 'style', label: this.labels.style || 'Build style', options: this.config.styleOptions });
    }

    return out;
  };

  /**
   * Economical builds exist only where there is wood to fix into. Alley needs
   * one wooden side, balcony needs all four surfaces wooden. Fenceline has no
   * style choice at all.
   */
  Hardware.prototype.styleOffered = function (type) {
    var a = this.answers;

    if (type === 'alley') return a.m1 === 'wood' || a.m2 === 'wood';
    if (type === 'balcony') {
      return a.m1 === 'wood' && a.m2 === 'wood' && a.m3 === 'wood' && a.m4 === 'wood';
    }
    return false;
  };

  Hardware.prototype.effectiveStyle = function (type) {
    if (!this.styleOffered(type)) return 'professional';
    return this.answers.style || 'economical';
  };

  /** Find the single rule matching the type and the answered options. */
  Hardware.prototype.matchRule = function (measure) {
    var type = this.ruleType(measure.area);
    var rules = this.rulesForType(type);
    if (!rules.length) return null;

    var a = this.answers;
    var style = this.effectiveStyle(type);
    var wanted;

    if (type === 'alley') {
      if (!a.m1 || !a.m2) return null;
      wanted = [measure.ends === 'one' ? 'one-end' : measure.ends === 'two' ? 'two-ends' : 'no-ends',
        alleyMaterials(a.m1, a.m2), style];
    } else if (type === 'balcony') {
      if (!a.m1 || !a.m2 || !a.m3 || !a.m4) return null;
      var allWood = a.m1 === 'wood' && a.m2 === 'wood' && a.m3 === 'wood' && a.m4 === 'wood';
      wanted = [allWood ? 'wood' : 'multi', style];
    } else if (type === 'fenceline') {
      if (!a.m1) return null;
      wanted = [(a.m1 || '').replace('other', 'colorbond')];
    } else {
      return null;
    }

    for (var i = 0; i < rules.length; i++) {
      var opts = rules[i].options;
      var same = opts.length === wanted.length;
      for (var o = 0; o < opts.length && same; o++) {
        if (opts[o] !== wanted[o]) same = false;
      }
      if (same) return rules[i];
    }

    return null;
  };

  Hardware.prototype.detail = function (id) {
    for (var i = 0; i < this.config.details.length; i++) {
      if (this.config.details[i].id === id) return this.config.details[i];
    }
    return null;
  };

  Hardware.prototype.product = function (handle) {
    return this.config.products[handle] || null;
  };

  /** The full hardware list for the current answers. */
  Hardware.prototype.build = function () {
    var measure = this.measure();
    if (!measure) return null;

    var rule = this.matchRule(measure);
    if (!rule) return null;

    var posts = parseFloat(this.answers.p);

    // Post count is a real input to the fenceline maths, not an optional extra.
    // Without it the calcs still evaluate, using zero, and quietly produce a
    // hardware list that is wrong rather than obviously incomplete.
    if (this.needsPosts(this.ruleType(measure.area)) && !(posts > 0)) return null;

    var vars = {
      l: measure.length || 0,
      w: measure.cover === 'height' ? measure.height || 0 : measure.width || 0,
      p: posts > 0 ? posts : 0,
      we1: measure.end1 ? measure.end1.width : 0,
      he1: measure.end1 ? measure.end1.height : 0,
      we2: measure.end2 ? measure.end2.width : 0,
      he2: measure.end2 ? measure.end2.height : 0
    };

    var items = [];
    var entries = rule.products.slice();

    /*
     * Mixed-material rules carry `multi_products`: per-material fixings whose
     * amount scales with how many metres of each material there are. Mirrors
     * the calculator's multi branch exactly: m1 and m2 contribute the width
     * each, m3 and m4 the length each, "other" counts as colorbond.
     */
    if (rule.multi_products && rule.multi_products.length) {
      var a = this.answers;
      var metres = {};

      function accumulate(material, amount) {
        if (!material) return;
        var key = material === 'other' ? 'colorbond' : material;
        metres[key] = (metres[key] || 0) + amount;
      }

      accumulate(a.m1, vars.w);
      accumulate(a.m2, vars.w);
      accumulate(a.m3, vars.l);
      accumulate(a.m4, vars.l);

      for (var mp = 0; mp < rule.multi_products.length; mp++) {
        var multi = rule.multi_products[mp];
        var total = metres[multi.selection_type];

        if (total > 0) {
          // Deliberately unparenthesised, mirroring the calculator's exact
          // string concatenation (entry.calc + " * " + total) so operator
          // precedence behaves identically if a calc ever contains + or -.
          // Exactness with the calculator beats prettier maths here.
          entries.push({
            id: multi.id,
            calc: multi.calc + ' * ' + total
          });
        }
      }
    }

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var detail = this.detail(entry.id);
      if (!detail) continue;

      var lines = resolve(entry, detail, vars);

      for (var li = 0; li < lines.length; li++) {
        var line = lines[li];
        var product = this.product(line.handle);
        if (!product) continue;

        var key = line.handle;
        var quantity = this.overrides.hasOwnProperty(key) ? this.overrides[key] : line.quantity;

        items.push({
          id: entry.id,
          handle: line.handle,
          sku: line.sku,
          title: product.title,
          image: product.image,
          url: product.url,
          variantId: product.variantId,
          price: product.price,
          suggested: line.quantity,
          quantity: quantity,
          total: product.price * quantity
        });
      }
    }

    return { rule: rule.name, vars: vars, items: items };
  };

  Hardware.prototype.needsPosts = function (type) {
    return type === 'fenceline';
  };

  Hardware.prototype.refresh = function () {
    if (!this.journey) return;

    var measure = this.measure();

    if (!measure) return;

    var supported = this.rulesForType(this.ruleType(measure.area)).length > 0;

    if (this.unsupportedEl) {
      this.unsupportedEl.hidden = supported;
      if (!supported) {
        this.unsupportedEl.textContent = (this.labels.unsupported || 'We cannot spec hardware for this area automatically.');
      }
    }

    if (this.questionsEl) this.questionsEl.hidden = !supported;
    if (this.resultEl) this.resultEl.hidden = !supported;

    if (!supported) {
      // Nothing to sell here, so let the customer past rather than trapping them.
      if (this.journey.data(this.stepId)) this.journey.clear(this.stepId);
      return;
    }

    this.renderIntro(measure);
    this.renderQuestions();
    this.renderResult();
  };

  Hardware.prototype.renderIntro = function (measure) {
    if (!this.introEl) return;

    var drop = measure.cover === 'height' ? measure.height : measure.width;
    var bits = ['Using your ' + measure.length + 'm by ' + drop + 'm ' + (measure.areaLabel || '').toLowerCase()];

    if (measure.ends === 'one') bits.push('with one enclosed end');
    if (measure.ends === 'two') bits.push('with two enclosed ends');

    this.introEl.textContent = bits.join(', ') + '. No need to measure again.';
    this.introEl.hidden = false;
  };

  Hardware.prototype.renderQuestions = function () {
    if (!this.questionsEl) return;

    var measure = this.measure();
    var questions = this.questions(measure.area);
    var html = '';

    for (var i = 0; i < questions.length; i++) {
      var q = questions[i];
      var value = this.answers[q.id] || '';

      html += '<div class="jhw__field"><label class="jhw__label" for="jhw-' + q.id + '">' + q.label + '</label>';

      if (q.help) {
        html += '<p class="jhw__help">' + q.help + '</p>';
      }

      if (q.type === 'number') {
        html +=
          '<div class="jhw__input-wrap"><input class="jhw__input" id="jhw-' + q.id + '" type="number"' +
          ' inputmode="numeric" min="' + q.min + '" max="' + q.max + '"' +
          ' data-hw-answer="' + q.id + '" value="' + value + '"></div>';
      } else {
        html += '<select class="jhw__select" id="jhw-' + q.id + '" data-hw-answer="' + q.id + '">';
        html += '<option value="">' + (this.labels.choose || 'Choose one') + '</option>';
        for (var o = 0; o < q.options.length; o++) {
          var opt = q.options[o];
          html +=
            '<option value="' + opt.value + '"' + (value === opt.value ? ' selected' : '') + '>' +
            opt.label + '</option>';
        }
        html += '</select>';
      }

      html += '</div>';
    }

    this.questionsEl.innerHTML = html;
  };

  Hardware.prototype.renderResult = function () {
    var result = this.build();

    if (!this.rowsEl) return;

    if (!result || !result.items.length) {
      this.rowsEl.innerHTML = '';
      if (this.resultEl) this.resultEl.hidden = true;
      this.report();
      return;
    }

    if (this.resultEl) this.resultEl.hidden = false;

    var html = '';
    for (var i = 0; i < result.items.length; i++) {
      var item = result.items[i];
      html +=
        '<div class="jhw__row" data-hw-row="' + item.handle + '">' +
          (item.image ? '<img class="jhw__row-image" src="' + item.image + '" alt="" loading="lazy">' : '<span class="jhw__row-image"></span>') +
          '<div class="jhw__row-main">' +
            '<a class="jhw__row-title" href="' + item.url + '" target="_blank" rel="noopener">' + item.title + '</a>' +
            '<span class="jhw__row-price">' + this.money(item.price) + ' each</span>' +
          '</div>' +
          '<input class="jhw__qty" type="number" min="0" max="999" value="' + item.quantity + '"' +
            ' data-hw-qty="' + item.handle + '" aria-label="Quantity of ' + item.title + '">' +
          '<span class="jhw__row-total" data-hw-row-total="' + item.handle + '">' + this.money(item.total) + '</span>' +
        '</div>';
    }

    this.rowsEl.innerHTML = html;
    this.renderTotals();
    this.report();
  };

  Hardware.prototype.renderTotals = function () {
    var result = this.build();
    if (!result) return;

    var total = 0;
    for (var i = 0; i < result.items.length; i++) {
      var item = result.items[i];
      total += item.total;

      var cell = this.root.querySelector('[data-hw-row-total="' + item.handle + '"]');
      if (cell) cell.textContent = this.money(item.total);
    }

    if (this.totalEl) this.totalEl.textContent = this.money(total);
  };

  Hardware.prototype.report = function () {
    if (!this.journey) return;

    var measure = this.measure();
    if (measure && !this.rulesForType(this.ruleType(measure.area)).length) return;

    var result = this.build();
    var items = result ? result.items.filter(function (i) {
      return i.quantity > 0;
    }) : [];

    if (!items.length) {
      if (this.journey.data(this.stepId)) this.journey.clear(this.stepId);
      return;
    }

    var total = 0;
    for (var i = 0; i < items.length; i++) total += items[i].total;

    this.journey.complete(this.stepId, {
      rule: result.rule,
      answers: this.answers,
      overrides: this.overrides,
      items: items,
      totalPrice: total
    });
  };

  // The raw engine, exposed so the test suite can sweep every rule in the
  // real metafield data through it and fail loudly on anything it cannot
  // handle. This is the parity guarantee with the standalone calculator.
  window.CatnetsHardwareEngine = { evaluate: evaluate, resolve: resolve };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-hw-config]')) new Hardware(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
